import { CommonModule } from "@angular/common";
import { Component, computed, inject, signal } from "@angular/core";
import { NgChartsModule } from "ng2-charts";
import ScheduleDetailHeader from "../shared/schedule-detail-header/schedule-detail-header.component";
import { JobDetailFooterComponent } from "../shared/job-detail-footer/job-detail-footer.component";
import { ActivatedRoute, Router } from "@angular/router";
import { Area, Assertion, ImageType } from "../../../../core/database/db";
import { ZoneDTO } from "../../../zones/models/ZoneDTO";
import { IssuesChartComponent } from "../shared/issues-chart/issues-chart.component";
import { SkeletonModule } from "primeng/skeleton";
import { BqStepDataServiceFactory } from "../../../../personas/shared/bq-step-data-service/bq-step-data-service-factory";
import { BqInspectionDataService } from "../../services/bq-inspection-data.service";
import { DummyBqInspectionDataService } from "../../services/dummy-bq-inspection-data-service";
import { UiToastService } from "../../../../shared/components/toast/ui-toast.service";
import { AssertionIssuesService, IssueWithDetails } from "../../../assertionIssues/services/assertion-issues.service";
import { NeedsAttentionItemComponent } from "../../../../personas/ccm/components/needs-attention-item/needs-attention-item.component";
import { AssertionService, AssertionWithDetails } from "../../../assertions/services/assertion.service";
import { LooksGoodItemComponent } from "../../../../personas/ccm/components/looks-good-item/looks-good-item.component";
import { DataUpdatedFadeDirective } from "../../../../shared/directives/data-updated-fade.directive";

@Component({
  selector: "app-job-final-review",
  imports: [
    CommonModule,
    NgChartsModule,
    ScheduleDetailHeader,
    JobDetailFooterComponent,
    IssuesChartComponent,
    SkeletonModule,
    NeedsAttentionItemComponent,
    LooksGoodItemComponent,
    DataUpdatedFadeDirective
  ],
  providers: [BqStepDataServiceFactory],
  templateUrl: "./job-final-review.component.html",
  styleUrl: "./job-final-review.component.scss"
})
export class JobFinalReviewComponent {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private bqStepDataServiceFactory: BqStepDataServiceFactory,
    private assertionIssueService: AssertionIssuesService,
    private assertionService: AssertionService
  ) { }
  private toastService = inject(UiToastService);
  private bqStepDataService = signal<BqInspectionDataService>(new DummyBqInspectionDataService(this.toastService));
  isRefreshingInBackground = computed(() => this.bqStepDataService()?.isRefreshingInBackground() ?? false);
  dataUpdatedPulse = computed(() => this.bqStepDataService()?.dataUpdatedPulse() ?? false);

  step = signal<number>(1);
  footerBarCompletedItems = signal<number>(1).asReadonly();
  footerBarTotalItems = signal<number>(1).asReadonly();

  dataServiceJob = computed(() => this.bqStepDataService()?.job() ?? (() => {
    throw new Error("Job data not available in BqInspectionDataService");
  })());
  dataServiceLot = computed(() => this.bqStepDataService()?.lot() ?? (() => {
    throw new Error("Lot data not available in BqInspectionDataService");
  })());
  dataServiceComponents = computed(() => this.bqStepDataService()?.components() ?? []);
  dataServiceAreas = computed(() => this.bqStepDataService()?.areas() ?? []);
  dataServiceAssertions = computed(() => this.bqStepDataService()?.assertions() ?? []);
  dataServiceIssues = computed(() => this.bqStepDataService()?.assertionIssues() ?? []);
  dataServiceMedia = computed(() => this.bqStepDataService()?.media() ?? []);
  dataServiceAreasConfig = computed(() => this.bqStepDataService()?.areasConfig() ?? []);
  dataServiceZones = computed(() => this.bqStepDataService()?.zones() ?? []);

  jobId = computed(() => this.dataServiceJob().id);
  assertions = computed(() => this.dataServiceAssertions());
  issues = computed(() => this.dataServiceIssues().filter(i => this.assertions().some(a => a.identifier === i.assertionIdentifier && i.deleted !== 1)));
  mediaForAssertionsAndIssues = computed(() =>
    this.dataServiceMedia().filter(m =>
      m.deleted !== 1 &&
      (
        this.assertions().some(a => a.identifier === m.parentIdentifier)
        || this.issues().some(i => i.identifier === m.parentIdentifier)
      )
    )
  );
  components = computed(() => this.dataServiceComponents().filter(c => this.assertions().some(a => a.componentId === c.id)));
  areas = computed(() => this.dataServiceAreas().filter(a => this.components().some(c => c.areaId === a.id)));
  issuesWithDetails = computed<IssueWithDetails[]>(() =>
    this.issues()
      .map(issue => this.assertionIssueService.createIssueWithDetails(issue, this.assertions(), this.components(), this.areas()))
  );
  isCcmInspection = computed(() => this.bqStepDataService()?.isCcmInspection() ?? false);

  minorIssues = computed(() => this.issuesWithDetails().filter(i => i.issue.severity === "minor"));
  majorIssues = computed(() => this.issuesWithDetails().filter(i => i.issue.severity === "major"));
  missingIssues = computed(() => this.issuesWithDetails().filter(i => i.issue.severity === "missing"));
  cmIssues = computed(() => this.issuesWithDetails().filter(i => i.issue.severity === "unset"));

  looksGoodItemsToReview = computed(() => {
    const isCcmInspection = this.isCcmInspection();
    // Assertions are lot-scoped, so a BQ6 photo from the CM sits on the same row the CCM answers in BQ7.
    const looksGoodImageType = isCcmInspection ? ImageType.LooksGoodCustomerCareManager : ImageType.LooksGoodConstructionManager;

    return this.assertions().filter(a => {
      const assertionHasNoRelatedIssues = !this.issuesWithDetails().find(i => i.issue.assertionIdentifier === a.identifier);
      const effectiveAssertionNote = isCcmInspection ? a.ccmNote : a.note;
      const assertionHasDetails = !!effectiveAssertionNote
        || this.mediaForAssertionsAndIssues().some(m => m.parentIdentifier === a.identifier && m.imageType === looksGoodImageType);

      return (isCcmInspection ? a.isCcmComplete : a.isComplete)
        && assertionHasNoRelatedIssues
        && assertionHasDetails;
    });
  });
  looksGoodItemsWithDetails = computed<AssertionWithDetails[]>(() =>
    this.looksGoodItemsToReview()
      .map(assertion => this.assertionService.createAssertionWithDetails(assertion, this.components(), this.areas(), this.mediaForAssertionsAndIssues(), this.isCcmInspection()))
  );

  subtitle = computed(() => `${this.step()} of 2`);
  nextButtonText = computed(() => (this.step() === 1 ? "Next" : "Finish & Upload"));
  hasIssues = computed(() => {
    const isCcmInspection = this.isCcmInspection();

    return isCcmInspection
      ? (
        this.minorIssues().length > 0
        || this.majorIssues().length > 0
        || this.missingIssues().length > 0
      )
      : this.cmIssues().length > 0;
  });
  hasLooksGoodItemsToReview = computed(() => this.looksGoodItemsToReview().length > 0);

  issueAssertionsMap: Map<number, Assertion> = new Map();
  areasByAssertionId: Map<number, Area> = new Map();

  zones: ZoneDTO[] = [];

  dataLoaded: boolean = false;

  async ngOnInit(): Promise<void> {
    const bqStep = this.route.snapshot.parent?.data["bqStep"] as string;
    this.bqStepDataService.set(this.bqStepDataServiceFactory.getService<BqInspectionDataService>(bqStep));

    this.step.set(parseInt(this.route.snapshot.queryParamMap.get("step") ?? "1"));

    this.dataLoaded = true;
  }


  goBack() {
    if (this.step() === 1) {
      this.router.navigate(["overview", "locations"], {
        relativeTo: this.route.parent
      });
    } else {
      this.step.set(1);
    }
  }

  nextClick() {
    if (this.step() === 1) {
      this.step.set(2);
    } else {
      this.submitClick();
    }
  }

  navToIssue(issueId: number, assertionIdentifier: string, isIssue: boolean = true) {
    const assertion = this.assertions().find(i => i.identifier === assertionIdentifier);
    const component = this.components().find(i => i.id === assertion?.componentId);
    const area = this.areas().find(i => i.id === component?.areaId)!;

    if (isIssue) {
      this.router.navigate(["area", area.id, "component", component!.id, "assertion", assertion!.id, "issues", issueId, "details"], {
        relativeTo: this.route.parent,
        queryParams: {
          from: "job-final-review"
        }
      });
    } else {
      this.router.navigate(["area", area.id, "component", component!.id, "assertion", assertion!.id, "details"], {
        relativeTo: this.route.parent,
        queryParams: {
          from: "job-final-review"
        }
      });
    }
  }

  submitClick() {
    this.router.navigate(["submission"], { relativeTo: this.route.parent });
  }
}
